import { z } from 'zod';
import { subscriptionStatusPgEnum, zodiacSignPgEnum } from '../../database';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;
const MAX_LIMIT = 100;
const NAME_MAX_LENGTH = 255;
const BODY_MAX_LENGTH = 1000;
const PROMPT_MAX_LENGTH = 10_000;
const CTA_MAX_LENGTH = 120;
const URL_MAX_LENGTH = 2048;
const MAX_ROLE_SWITCH_TTL_MINUTES = 120;
const MAX_EMAIL_LOG_LIMIT = 200;

const TRACKING_ID_MIN_LENGTHS = {
  gtmId: 4,
  fbPixelId: 4,
  googleAdsId: 2,
  googleAdsLabel: 2,
  mixpanelToken: 10,
  clarityId: 4,
  tiktokPixelId: 4,
} as const;

export const adminUserListInputSchema = z.object({
  page: z.number().min(DEFAULT_PAGE).default(DEFAULT_PAGE),
  limit: z.number().min(1).max(MAX_LIMIT).default(DEFAULT_LIMIT),
  searchQuery: z.string().optional(),
  subscriptionStatus: subscriptionStatusPgEnum.optional(),
  zodiacSign: zodiacSignPgEnum.optional(),
});

export type AdminUserListInput = z.infer<typeof adminUserListInputSchema>;

export const adminGetUserByIdSchema = z.object({ id: z.string() });
export const adminUserIdSchema = z.object({ userId: z.string() });

export type AdminGetUserByIdInput = z.infer<typeof adminGetUserByIdSchema>;

export const adminUpdateUserSchema = z.object({
  userId: z.string(),
  name: z.string().min(2),
  email: z.string().email(),
  role: z.enum(['user', 'admin']),
});

export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

export const adminUpdateSubscriptionStatusSchema = z.object({
  userId: z.string(),
  status: subscriptionStatusPgEnum,
});

export type AdminUpdateSubscriptionStatusInput = z.infer<
  typeof adminUpdateSubscriptionStatusSchema
>;

export const adminAddRewardsSchema = z.object({
  userId: z.string(),
  cosmicDust: z.number().int().min(0),
  xp: z.number().int().min(0),
});

export type AdminAddRewardsInput = z.infer<typeof adminAddRewardsSchema>;

export const adminTrackingConfigSchema = z.object({
  gtmId: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.gtmId).optional(),
  fbPixelId: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.fbPixelId).optional(),
  googleAdsId: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.googleAdsId).optional(),
  googleAdsLabel: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.googleAdsLabel).optional(),
  mixpanelToken: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.mixpanelToken).optional(),
  clarityId: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.clarityId).optional(),
  tiktokPixelId: z.string().trim().min(TRACKING_ID_MIN_LENGTHS.tiktokPixelId).optional(),
  enableServerConversions: z.boolean().optional(),
  fbAccessToken: z.string().trim().optional(),
  tiktokAccessToken: z.string().trim().optional(),
  tiktokTestEventCode: z.string().trim().optional(),
  gtmServerEndpoint: z
    .string()
    .url()
    .refine((value) => {
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password;
      } catch {
        return false;
      }
    }, 'GTM server endpoint must be HTTPS without credentials')
    .optional(),
});

export type AdminTrackingConfigInput = z.infer<typeof adminTrackingConfigSchema>;

export const adminNotificationListSchema = z.object({
  page: z.number().min(DEFAULT_PAGE).default(DEFAULT_PAGE),
  limit: z.number().min(1).max(MAX_LIMIT).default(DEFAULT_LIMIT),
  notificationType: z.enum(['DAILY_HOROSCOPE', 'DAILY_TAROT', 'STREAK_REMINDER']).optional(),
  isActive: z.boolean().optional(),
});

export const adminNotificationTemplateIdSchema = z.object({ id: z.string() });

export const adminCreateNotificationSchema = z.object({
  name: z.string().min(1).max(NAME_MAX_LENGTH),
  notificationType: z.enum(['DAILY_HOROSCOPE', 'DAILY_TAROT', 'STREAK_REMINDER']),
  title: z.string().min(1).max(NAME_MAX_LENGTH),
  body: z.string().min(1).max(BODY_MAX_LENGTH),
  targetUrl: z.string().url().optional(),
  scheduleTime: z.string().min(1),
  isActive: z.boolean().default(true),
});

export const adminUpdateNotificationSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(NAME_MAX_LENGTH).optional(),
  notificationType: z.enum(['DAILY_HOROSCOPE', 'DAILY_TAROT', 'STREAK_REMINDER']).optional(),
  title: z.string().min(1).max(NAME_MAX_LENGTH).optional(),
  body: z.string().min(1).max(BODY_MAX_LENGTH).optional(),
  targetUrl: z.string().url().optional(),
  scheduleTime: z.string().min(1).optional(),
  isActive: z.boolean().optional(),
});

export const adminCacheSummaryInputSchema = z.object({
  date: z.string().optional(),
});

export const adminCacheListInputSchema = z.object({
  page: z.number().min(1).default(1),
  searchQuery: z.string().optional(),
});

export const adminCacheDeleteInputSchema = z.object({ key: z.string() });

export const adminPromptUpdateSchema = z.object({
  id: z.string(),
  content: z.string().min(1),
  systemInstruction: z.string().min(1),
});

export const adminImpersonationTokenSchema = z.object({ token: z.string() });

export const adminRoleSwitchSchema = z.object({
  role: z.enum(['user', 'logged-out']),
  subscriptionStatus: subscriptionStatusPgEnum.optional(),
  ttlMinutes: z.number().int().min(1).max(MAX_ROLE_SWITCH_TTL_MINUTES).optional(),
});

export const adminUpdateTarotCardSchema = z.object({
  id: z.string(),
  name: z.string(),
  keywords: z.array(z.string()),
  meanings: z.object({
    light: z.array(z.string()),
    shadow: z.array(z.string()),
  }),
});

export const adminUpdateQuestSchema = z.object({
  id: z.string(),
  description: z.string(),
  reward: z.number().int(),
});

export const emailCampaignStatusEnum = z.enum(['draft', 'active', 'paused', 'archived']);
export const emailCampaignAudienceEnum = z.enum(['free_only']);
export const emailCampaignTimezoneModeEnum = z.enum(['user', 'utc']);

export const adminEmailCampaignListSchema = z.object({
  page: z.number().min(DEFAULT_PAGE).default(DEFAULT_PAGE),
  limit: z.number().min(1).max(MAX_LIMIT).default(DEFAULT_LIMIT),
  status: emailCampaignStatusEnum.optional(),
});

export const adminEmailCampaignIdSchema = z.object({ id: z.string() });

export const adminEmailCampaignCreateSchema = z.object({
  name: z.string().min(1).max(NAME_MAX_LENGTH),
  status: emailCampaignStatusEnum.optional(),
  startsAt: z.string().optional(),
  rolloutStartAt: z.string(),
  onlyNewUsers: z.boolean().optional(),
  audience: emailCampaignAudienceEnum.optional(),
  timezoneMode: emailCampaignTimezoneModeEnum.optional(),
});

export const adminEmailCampaignUpdateSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(NAME_MAX_LENGTH).optional(),
  status: emailCampaignStatusEnum.optional(),
  startsAt: z.string().optional().nullable(),
  rolloutStartAt: z.string().optional().nullable(),
  onlyNewUsers: z.boolean().optional(),
  audience: emailCampaignAudienceEnum.optional(),
  timezoneMode: emailCampaignTimezoneModeEnum.optional(),
});

export const adminEmailCampaignStepListSchema = z.object({ campaignId: z.string() });

export const adminEmailCampaignStepIdSchema = z.object({ id: z.string() });

export const adminEmailCampaignStepCreateSchema = z.object({
  campaignId: z.string(),
  stepKey: z.string().min(1).max(NAME_MAX_LENGTH),
  delayMinutes: z.number().int().min(0),
  templateKey: z.string().min(1).max(NAME_MAX_LENGTH),
  promptId: z.string().optional().nullable(),
  promptText: z.string().min(1).max(PROMPT_MAX_LENGTH),
  isActive: z.boolean().optional(),
  subjectFallback: z.string().min(1).max(NAME_MAX_LENGTH),
  previewFallback: z.string().min(1).max(BODY_MAX_LENGTH),
  ctaFallback: z.string().min(1).max(CTA_MAX_LENGTH),
  deepLinkFallback: z.string().min(1).max(URL_MAX_LENGTH),
  aiSchemaVersion: z.string().optional(),
  orderIndex: z.number().int().optional(),
});

export const adminEmailCampaignStepUpdateSchema = z.object({
  id: z.string(),
  stepKey: z.string().min(1).max(NAME_MAX_LENGTH).optional(),
  delayMinutes: z.number().int().min(0).optional(),
  templateKey: z.string().min(1).max(NAME_MAX_LENGTH).optional(),
  promptId: z.string().optional().nullable(),
  promptText: z.string().min(1).max(PROMPT_MAX_LENGTH).optional(),
  isActive: z.boolean().optional(),
  subjectFallback: z.string().min(1).max(NAME_MAX_LENGTH).optional(),
  previewFallback: z.string().min(1).max(BODY_MAX_LENGTH).optional(),
  ctaFallback: z.string().min(1).max(CTA_MAX_LENGTH).optional(),
  deepLinkFallback: z.string().min(1).max(URL_MAX_LENGTH).optional(),
  aiSchemaVersion: z.string().optional(),
  orderIndex: z.number().int().optional(),
});

export const adminEmailCampaignLogListSchema = z.object({
  campaignId: z.string(),
  limit: z.number().int().min(1).max(MAX_EMAIL_LOG_LIMIT).optional(),
});

export const adminEmailCampaignPreviewStepSchema = z.object({
  stepId: z.string(),
});

export const adminEmailCampaignPreviewAllSchema = z.object({
  campaignId: z.string(),
  force: z.boolean().optional(),
});

export const adminEmailCampaignUserLogListSchema = z.object({
  userId: z.string(),
  limit: z.number().int().min(1).max(MAX_EMAIL_LOG_LIMIT).optional(),
});

export const adminEmailPreferencesUpdateSchema = z.object({
  userId: z.string(),
  optOut: z.boolean(),
});
